import { ApolloError } from '@apollo/client';

import type { NovaAnalyticsScope, NovaAnalyticsScopeInput } from '../../../../queries/nova-analytics';

export function analyticsScopeKey(scope: NovaAnalyticsScope): string {
  return `${scope.organization.id}:${scope.type}:${scope.id}`;
}

export function analyticsScopeInput(scope: NovaAnalyticsScope): NovaAnalyticsScopeInput {
  return { organizationId: scope.organization.id, ...(scope.type === 'TEAM' ? { teamId: scope.id } : {}) };
}

export function findAnalyticsScope(scopes: NovaAnalyticsScope[], input: Partial<NovaAnalyticsScopeInput>) {
  if (!input.organizationId || input.teamId === '') return undefined;
  return scopes.find(
    (scope) =>
      scope.organization.id === input.organizationId &&
      (input.teamId ? scope.type === 'TEAM' && scope.id === input.teamId : scope.type === 'ORGANIZATION'),
  );
}

export function sortedAnalyticsScopes(scopes: NovaAnalyticsScope[]): NovaAnalyticsScope[] {
  return [...scopes].sort(
    (a, b) =>
      a.organization.name.localeCompare(b.organization.name) ||
      a.organization.id.localeCompare(b.organization.id) ||
      (a.type === b.type ? 0 : a.type === 'ORGANIZATION' ? -1 : 1) ||
      a.name.localeCompare(b.name) ||
      a.id.localeCompare(b.id),
  );
}

export function analyticsSectionError(error: ApolloError | undefined, field: string): ApolloError | undefined {
  if (!error) return undefined;
  const graphQLErrors = error.graphQLErrors.filter((item) => !item.path || item.path[0] === field);
  return graphQLErrors.length || error.networkError
    ? new ApolloError({ graphQLErrors, networkError: error.networkError })
    : undefined;
}

export function analyticsErrorCode(error?: ApolloError): string | undefined {
  const codes = error?.graphQLErrors.map((item) => item.extensions?.code);
  // Access failures always win over partial successes.
  for (const code of ['UNAUTHENTICATED', 'FORBIDDEN', 'BAD_USER_INPUT']) if (codes?.includes(code)) return code;
  const network = error?.networkError;
  if (network && 'statusCode' in network) {
    if (network.statusCode === 401) return 'UNAUTHENTICATED';
    if (network.statusCode === 403) return 'FORBIDDEN';
  }
  return codes?.find((code): code is string => typeof code === 'string');
}

export function invalidatesAnalyticsScope(error?: ApolloError): boolean {
  return ['UNAUTHENTICATED', 'FORBIDDEN', 'BAD_USER_INPUT'].includes(analyticsErrorCode(error) ?? '');
}

export function analyticsErrorMessage(error?: ApolloError): string | undefined {
  switch (analyticsErrorCode(error)) {
    case 'ANALYTICS_POLICY_PENDING':
      return 'Organization-wide details are not available yet while reporting permissions are being finalized.';
    case 'ANALYTICS_SCOPE_INCOMPLETE':
      return 'The full membership of this selection is not available in analytics. Try an available team or retry after its membership and analytics identities are resolved.';
    case 'UNAUTHENTICATED':
      return 'Your session has expired. Sign in again to view analytics.';
    case 'FORBIDDEN':
      return 'You no longer have reporting access to this selection. Choose an available organization or team.';
    case 'BAD_USER_INPUT':
      return 'This reporting selection is invalid. Choose an available organization or team.';
    case 'ANALYTICS_DISABLED':
      return 'Live Nova analytics are currently unavailable.';
    default:
      return undefined;
  }
}
